import { useEffect, useRef, useState } from 'react'
import { BellRing } from 'lucide-react'
import { usePendingHandoffs } from '../../features/shift-handover/hooks/usePendingHandoffs'
import { useShiftMutations } from '../../features/shift-handover/hooks/useShiftMutations'
import { useAuthStore } from '../../features/auth/store/authStore'
import { isHandoffManager } from '../../features/shift-handover/utils/shiftPermissions'
import { DeclineModal } from '../../features/shift-handover/components/DeclineModal'

export function PendingHandoffsBell(): JSX.Element | null {
  const user = useAuthStore((s) => s.user)
  const [open, setOpen] = useState(false)
  const [decliningId, setDecliningId] = useState<number | null>(null)
  const { handoffs, isForbidden } = usePendingHandoffs()
  const { acknowledge, decline } = useShiftMutations()
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    function onMouseDown(e: MouseEvent): void {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onMouseDown)
    return () => document.removeEventListener('mousedown', onMouseDown)
  }, [open])

  if (!isHandoffManager(user) || isForbidden) return null

  return (
    <div className="relative" ref={containerRef}>
      <button type="button" onClick={() => setOpen((v) => !v)} className="relative rounded-full p-2 hover:bg-black/5" aria-label="Pending handoffs">
        <BellRing className="h-5 w-5 text-gray-700" />
        {handoffs.length > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
            {handoffs.length}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 z-50 mt-0.5 w-80 rounded-xl border border-gray-200 bg-white p-2 shadow-lg">
          {handoffs.length === 0 ? (
            <p className="p-3 text-sm text-gray-400">There are no new notifications</p>
          ) : (
            handoffs.map((h) => (
              <div key={h.id} className="border-b border-gray-100 p-3 last:border-0">
                <p className="text-sm font-medium text-gray-900">{h.from_supervisor_name ?? 'A supervisor'} wants to hand off</p>
                <p className="text-xs text-gray-400">Property #{h.property_id} · {new Date(h.created_at).toLocaleString()}</p>
                <div className="mt-2 flex gap-2">
                  <button type="button" onClick={() => acknowledge.mutate({ handoffId: h.id, propertyId: h.property_id })} className="rounded-lg bg-brand-green px-3 py-1 text-xs font-semibold text-white">Accept</button>
                  <button type="button" onClick={() => setDecliningId(h.id)} className="rounded-lg border border-gray-200 px-3 py-1 text-xs font-medium text-gray-700">Decline</button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      <DeclineModal
        open={decliningId !== null}
        onClose={() => setDecliningId(null)}
        isSubmitting={decline.isPending}
        onSubmit={(reason) => {
          if (decliningId === null) return
          decline.mutate({ handoffId: decliningId, reason }, { onSuccess: () => setDecliningId(null) })
        }}
      />
    </div>
  )
}
